import React, { useState, useEffect } from 'react';
import './Header.css';
import api from '../services/api';

const Header = ({ userData, onLogout, onProfileClick, onChangePasswordClick, toggleTheme, theme }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  const [showWebOrders, setShowWebOrders] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [cancelReason, setCancelReason] = useState('');
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [pendingOrdersLoading, setPendingOrdersLoading] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedOrderForMap, setSelectedOrderForMap] = useState(null);

  // Check fullscreen status
  useEffect(() => {
    const checkFullscreen = () => {
      setIsFullscreen(!!(document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement));
    };

    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('msfullscreenchange', checkFullscreen);
    checkFullscreen();

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('msfullscreenchange', checkFullscreen);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch pending orders
  useEffect(() => {
    const fetchPendingOrders = async () => {
      try {
        setPendingOrdersLoading(true);
        const response = await api.get('api/ecommerce/orders/status/pending');
        if (response.data.data?.orders?.data) {
          setPendingOrders(response.data.data.orders.data);
        }
      } catch (error) {
        console.error('Error fetching pending orders:', error);
      } finally {
        setPendingOrdersLoading(false);
      }
    };

    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter orders
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order =>
        order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_phone?.includes(searchTerm) ||
        order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  // Fetch orders by status
  const fetchOrdersByStatus = async (status) => {
    setLoading(true);
    setSearchTerm('');
    try {
      const response = await api.get(`api/ecommerce/orders/status/${status}`);
      if (response.data.data?.orders?.data) {
        setOrders(response.data.data.orders.data);
        setFilteredOrders(response.data.data.orders.data);
      } else {
        setOrders([]);
        setFilteredOrders([]);
      }
    } catch (error) {
      console.error(`Error fetching ${status} orders:`, error);
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus, reason = '') => {
    try {
      setApiLoading(true);
      const requestData = { status: newStatus };
      if (newStatus === 'cancelled' && reason) {
        requestData.cancellation_reason = reason;
      }

      await api.put(`api/ecommerce/orders/${orderId}/status`, requestData);
      fetchOrdersByStatus(currentStatus);

      // Refresh pending orders count
      const pendingResponse = await api.get('api/ecommerce/orders/status/pending');
      if (pendingResponse.data.data?.orders?.data) {
        setPendingOrders(pendingResponse.data.data.orders.data);
      }

      // Close cancel modal
      if (newStatus === 'cancelled') {
        setOrderToCancel(null);
        setCancelReason('');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    } finally {
      setApiLoading(false);
    }
  };

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
  };

  const confirmCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert('Please enter a cancellation reason');
      return;
    }
    await updateOrderStatus(orderToCancel.id, 'cancelled', cancelReason.trim());
  };

  // Check if order is home delivery
  const isHomeDelivery = (order) => {
    return order.latitude && order.longitude;
  };

  // Get delivery type badge
  const getDeliveryTypeBadge = (order) => {
    return isHomeDelivery(order) ? '🏠 Home Delivery' : '🏪 Store Pickup';
  };

  // Open Google Maps for delivery address
  const openGoogleMaps = (order) => {
    if (isHomeDelivery(order)) {
      const { latitude, longitude, customer_address } = order;
      const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(mapsUrl, '_blank');
    }
  };

  // Show map in modal
  const showMapModalForOrder = (order) => {
    if (isHomeDelivery(order)) {
      setSelectedOrderForMap(order);
      setShowMapModal(true);
    }
  };

  // Generate Google Maps embed URL
  const getGoogleMapsEmbedUrl = (order) => {
    if (!order.latitude || !order.longitude) return null;
    return `https://maps.google.com/maps?q=${order.latitude},${order.longitude}&z=15&output=embed`;
  };

  const printBill = async (order) => {
    try {
      setApiLoading(true);
      // Your existing printBill function remains the same
      const billWindow = window.open('', '_blank', 'width=300,height=600');
      // ... rest of printBill function
    } catch (error) {
      console.error('Error printing bill:', error);
    } finally {
      setApiLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    } else {
      const elem = document.documentElement;
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
    }
  };

  const handleWebOrdersClick = () => {
    setShowWebOrders(true);
    setCurrentStatus('pending');
    fetchOrdersByStatus('pending');
  };

  const handleStatusChange = (status) => {
    setCurrentStatus(status);
    fetchOrdersByStatus(status);
  };

  const handleLogout = async () => {
    try {
      setApiLoading(true);
      await onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setApiLoading(false);
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (orderToCancel) {
          setOrderToCancel(null);
          setCancelReason('');
        } else if (showMapModal) {
          setShowMapModal(false);
          setSelectedOrderForMap(null);
        } else if (showWebOrders) {
          setShowWebOrders(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showWebOrders, orderToCancel, showMapModal]);

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      confirmed: '#3b82f6',
      processing: '#8b5cf6',
      ready_for_pickup: '#10b981',
      ready_for_delivery: '#059669',
      out_for_delivery: '#f97316',
      delivered: '#047857',
      picked_up: '#0d9488',
      cancelled: '#dc2626'
    };
    return colors[status] || '#6b7280';
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      pending: ['ready_for_pickup', 'ready_for_delivery', 'cancelled'],
      ready_for_pickup: ['picked_up', 'cancelled'],
      ready_for_delivery: ['out_for_delivery', 'cancelled'],
      out_for_delivery: ['delivered', 'cancelled']
    };
    return statusFlow[currentStatus] || [];
  };

  const formatCurrency = (amount) => {
    return `LKR ${parseFloat(amount).toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatStatusText = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <header className="pos-header">
      {/* Global Loader Overlay */}
      {apiLoading && (
        <div className="global-loader-overlay">
          <div className="global-loader">
            <div className="loader-spinner"></div>
            <p>Processing...</p>
          </div>
        </div>
      )}

      <div className="header-left">
        <h1 className="store-name">Light Grocer Super Market</h1>
        <div className="user-info">
          <span>Welcome, </span>
          <span className="user-name">{userData.employer.full_legal_name}</span>
          <span className="user-role">({userData.employer.user_role})</span>
        </div>
      </div>

      <div className="header-center">
        <button
          className="web-orders-btn"
          onClick={handleWebOrdersClick}
          disabled={pendingOrdersLoading}
        >
          {pendingOrdersLoading ? (
            <div className="button-loader">
              <div className="small-spinner"></div>
              Loading...
            </div>
          ) : (
            <>
              📦 Web Orders
              {pendingOrders.length > 0 && (
                <span className="pending-count">
                  {pendingOrders.length}
                </span>
              )}
            </>
          )}
        </button>
      </div>

      <div className="header-right">
        <button onClick={toggleTheme} className="theme-toggle-btn" title="Toggle Theme" disabled={apiLoading}>
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <div className="shift-info">
          <span className="shift-status">Shift: {userData.current_shift.status}</span>
          <span className="shift-time real-time-clock">{currentTime}</span>
        </div>

        <button
          className="fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          disabled={apiLoading}
        >
          {isFullscreen ? '⤵️' : '⤴️'}
        </button>

        <div className="user-menu">
          <button
            className="user-menu-btn"
            onClick={() => setShowMenu(!showMenu)}
            disabled={apiLoading}
          >
            👤 Menu
          </button>

          {showMenu && (
            <div className="dropdown-menu">
              <button
                className="dropdown-item"
                onClick={() => {
                  onProfileClick();
                  setShowMenu(false);
                }}
                disabled={apiLoading}
              >
                👤 View Profile
              </button>
              <button
                className="dropdown-item"
                onClick={() => {
                  onChangePasswordClick();
                  setShowMenu(false);
                }}
                disabled={apiLoading}
              >
                🔒 Change Password
              </button>
              <button
                className="dropdown-item logout"
                onClick={handleLogout}
                disabled={apiLoading}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Web Orders Modal */}
      {showWebOrders && (
        <div className="modal-overlay">
          <div className="web-orders-modal">
            <div className="modal-header">
              <h3>Web Orders Management</h3>
              <button
                className="close-modal"
                onClick={() => setShowWebOrders(false)}
                disabled={apiLoading}
              >
                ×
              </button>
            </div>

            <div className="search-container">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  disabled={loading}
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    onClick={clearSearch}
                    disabled={loading}
                  >
                    ×
                  </button>
                )}
                <span className="search-icon">🔍</span>
              </div>
              <div className="search-results-info">
                Showing {filteredOrders.length} of {orders.length} orders
                {searchTerm && ` for "${searchTerm}"`}
              </div>
            </div>

            <div className="status-tabs" style={{ margin: '10px', height: 150 }}>
              {[
                'pending', 'ready_for_pickup',
                'ready_for_delivery', 'out_for_delivery', 'delivered',
                'picked_up', 'cancelled'
              ].map(status => (
                <button
                  key={status}
                  className={`status-tab ${currentStatus === status ? 'active' : ''}`}
                  onClick={() => handleStatusChange(status)}
                  style={{
                    borderBottomColor: currentStatus === status ? getStatusColor(status) : 'transparent'
                  }}
                  disabled={loading}
                >
                  {formatStatusText(status)}
                </button>
              ))}
            </div>

            <br></br>

            <div className="orders-container">
              {loading ? (
                <div className="loading-container">
                  <div className="loader-spinner"></div>
                  <p>Loading orders...</p>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="no-orders">
                  {searchTerm ? 'No orders found matching your search' : `No ${formatStatusText(currentStatus)} orders`}
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <div className="order-info">
                        <h4>Order #{order.order_number}</h4>
                        <span className="delivery-type-badge">
                          {getDeliveryTypeBadge(order)}
                        </span>
                        <span className="customer-name">
                          {order.customer_first_name} {order.customer_last_name}
                        </span>
                        <span className="customer-phone">{order.customer_phone}</span>
                        {order.is_urgent && (
                          <span className="urgent-badge">URGENT</span>
                        )}
                      </div>
                      <div className="order-total">
                        {formatCurrency(order.total_amount)}
                        <button
                          className="print-bill-btn"
                          onClick={() => printBill(order)}
                          disabled={apiLoading}
                        >
                          {apiLoading ? '🔄' : '🖨️'} Print
                        </button>
                      </div>
                    </div>

                    <div className="order-details">
                      <div className="order-items">
                        <strong>Items:</strong>
                        {order.items.map(item => (
                          <div key={item.id} className="order-item">
                            {item.quantity}x {item.product_name}
                            {item.variant_name && ` (${item.variant_name})`}
                            - {formatCurrency(item.total_price)}
                          </div>
                        ))}
                      </div>

                      <div className="order-meta">
                        <div><strong>Payment:</strong> {order.payment_method}</div>
                        {order.delivery_method && (
                          <div><strong>Delivery:</strong> {order.delivery_method}</div>
                        )}
                        {order.customer_address && (
                          <div className="address-container">
                            <strong>Address:</strong> 
                            <span className="customer-address">{order.customer_address}</span>
                            {isHomeDelivery(order) && (
                              <div className="map-actions">
                                <button
                                  className="map-btn small"
                                  onClick={() => openGoogleMaps(order)}
                                  title="Open in Google Maps"
                                  disabled={apiLoading}
                                >
                                  🗺️ Open Maps
                                </button>
                                <button
                                  className="map-btn small"
                                  onClick={() => showMapModalForOrder(order)}
                                  title="View Map"
                                  disabled={apiLoading}
                                >
                                  📍 View Map
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {isHomeDelivery(order) && (
                          <>
                            <div><strong>Distance:</strong> {order.delivery_distance} km</div>
                            <div><strong>Delivery Fee:</strong> {formatCurrency(order.delivery_fee_amount)}</div>
                          </>
                        )}
                      </div>
                    </div>

                    {getNextStatus(order.status).length > 0 && (
                      <div className="status-actions">
                        {getNextStatus(order.status).map(nextStatus => (
                          nextStatus === 'cancelled' ? (
                            <button
                              key={nextStatus}
                              className="status-btn cancel-btn"
                              onClick={() => handleCancelOrder(order)}
                              disabled={apiLoading}
                            >
                              Cancel Order
                            </button>
                          ) : (
                            <button
                              key={nextStatus}
                              className="status-btn"
                              onClick={() => updateOrderStatus(order.id, nextStatus)}
                              style={{ backgroundColor: getStatusColor(nextStatus) }}
                              disabled={apiLoading}
                            >
                              {apiLoading ? 'Updating...' : `Mark as ${formatStatusText(nextStatus)}`}
                            </button>
                          )
                        ))}
                      </div>
                    )}

                    <div
                      className="current-status"
                      style={{ color: getStatusColor(order.status) }}
                    >
                      Status: <strong>{formatStatusText(order.status)}</strong>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reason Modal */}
      {orderToCancel && (
        <div className="modal-overlay cancel-modal-overlay">
          <div className="cancel-reason-modal">
            <div className="modal-header">
              <h3>Cancel Order #{orderToCancel.order_number}</h3>
              <button
                className="close-modal"
                onClick={() => {
                  setOrderToCancel(null);
                  setCancelReason('');
                }}
                disabled={apiLoading}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>
                Cancelling order for {orderToCancel.customer_first_name} {orderToCancel.customer_last_name}
              </p>

              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter cancellation reason..."
                className="cancel-reason-input"
                rows="4"
                disabled={apiLoading}
              />

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setOrderToCancel(null);
                    setCancelReason('');
                  }}
                  disabled={apiLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={confirmCancelOrder}
                  disabled={!cancelReason.trim() || apiLoading}
                >
                  {apiLoading ? (
                    <div className="button-loader">
                      <div className="small-spinner"></div>
                      Cancelling...
                    </div>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMapModal && selectedOrderForMap && (
        <div className="modal-overlay">
          <div className="map-modal">
            <div className="modal-header">
              <h3>
                Delivery Location - Order #{selectedOrderForMap.order_number}
              </h3>
              <button
                className="close-modal"
                onClick={() => {
                  setShowMapModal(false);
                  setSelectedOrderForMap(null);
                }}
                disabled={apiLoading}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="customer-info">
                <p><strong>Customer:</strong> {selectedOrderForMap.customer_first_name} {selectedOrderForMap.customer_last_name}</p>
                <p><strong>Address:</strong> {selectedOrderForMap.customer_address}</p>
                <p><strong>Phone:</strong> {selectedOrderForMap.customer_phone}</p>
                <p><strong>Distance:</strong> {selectedOrderForMap.delivery_distance} km</p>
              </div>

              <div className="map-container">
                {getGoogleMapsEmbedUrl(selectedOrderForMap) ? (
                  <iframe
                    src={getGoogleMapsEmbedUrl(selectedOrderForMap)}
                    width="100%"
                    height="400"
                    style={{ border: 0, borderRadius: '8px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Delivery Location Map"
                  />
                ) : (
                  <div className="no-map-available">
                    <p>Map not available for this location</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => openGoogleMaps(selectedOrderForMap)}
                  disabled={apiLoading}
                >
                  🗺️ Open in Google Maps
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowMapModal(false);
                    setSelectedOrderForMap(null);
                  }}
                  disabled={apiLoading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;